import { useMemo } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import {
  useLocationFieldConfig,
  useOfficeFieldConfig,
  useNoteTakerConfig,
  useDurationFieldConfig,
  useHostFieldConfig,
  useMeetingRoomFieldConfig,
  type MeetingRoomGroup,
} from '@/formConfigs';
import { useInterviewerField } from './useInterviewerField';
import type {
  ScheduleMode,
  ApiReference,
} from '../../../config/schedule.types';
import { SCHEDULE_INTERVIEW_COPY } from '../../../config/schedule.copy';
import { ScheduleCalendarRowField } from '../fields/ScheduleCalendarRowField';
import { CandidatePhoneField } from '../fields/CandidatePhoneField';
import {
  renderMeetingRoomGroupHeader,
  renderMeetingRoomOption,
  renderMeetingRoomTag,
  renderMeetingRoomTriggerValue,
} from '../fields/MeetingRoomRenders';
import {
  DateTimeField,
  TIME_OPTIONS,
  parse12hrTime,
  formatTime12,
  findClosestTimeOption,
} from '../fields/dateTime';
import moment from 'moment-timezone';
import { useHackerRankTemplates } from '../../../../InterviewActivity/details/useHackerRankTemplates';
import { useSuggestedInterviewer } from '@/api/suggestedInterviewer';
import {
  InterviewerStatusBanner,
  RecommendedInterviewerPill,
  type InterviewerStatusMap,
} from '../fields/interviewer';

const isDateTimeEditingCondition = {
  operator: 'equal' as const,
  field: 'isDateTimeEditing',
  value: 'edit',
  preserveValue: true,
};

const copy = SCHEDULE_INTERVIEW_COPY.details;

const INTERVIEW_TYPE_OPTIONS = [
  { value: 'one_on_one', label: 'One-on-one interview' },
  { value: 'panel', label: 'Panel interview' },
];

interface UseDetailsFormSchemaOptions {
  mode: ScheduleMode;
  apiReference?: ApiReference;
  interviewPlan?: Record<string, unknown>;
  /** From interview plan — controls meeting room checkbox visibility */
  meetingRoomsEnabled?: boolean;
  /** Current interview type — controls single vs multi interviewer select */
  interviewType?: string;
  /** Currently selected start date — for filtering past time options */
  selectedStartDate?: Date | null;
  /** Currently selected start time — for filtering end time options */
  selectedStartTime?: string;
  /** Calendar panel timezone — "today" and minDate must match the schedule TZ */
  scheduleTimezone?: string;
  /**
   * When the calendar updates the slot via setValue (no onFieldChange), schema
   * still needs the booked start for minDate / time-option filtering.
   */
  calendarBookedStartIso?: string | null;
  /** Paired with calendarBookedStartIso for the AI recommendation call. */
  calendarBookedEndIso?: string | null;
  /** ISO start of the visible calendar week. The interviewer dropdown
   *  uses it to build the `week_start_date` API param and the
   *  "Interview load (<range>)" header so both update when the user
   *  navigates between weeks on the calendar. */
  visibleWeekStartIso?: string | null;
  /** IDs of selected interviewers whose calendar is NOT connected —
   *  the chip renders a warning icon with a hover-tooltip when a
   *  participant_id matches. Populated from the busy-events fetch. */
  disconnectedInterviewerIds?: number[];
  /**
   * Pre-shaped meeting-room groups (office → rooms with `status` +
   * `capacity`). When present the meeting-room select renders the
   * accordion-grouped Figma recipe; when absent it falls back to a
   * plain flat list.
   */
  meetingRoomGroups?: MeetingRoomGroup[];
  /** Spinner on the meeting-room select while availability polls. */
  meetingRoomsLoading?: boolean;
  /** Candidate name label for the phone_interview read-only block. */
  candidateName?: string;
  /** Candidate avatar URL for the phone_interview read-only block. */
  candidateAvatar?: string;
  /** Pre-formatted candidate phone for the phone_interview read-only block. */
  candidatePhone?: string;
  /** For the AI-suggested interviewer call (ats_job_stage_action_id). */
  atsJobStageActionId?: number;
  /** Pre-resolved interviewer options for re-rendering the selected
   *  chip in edit mode (async-select can't resolve names from bare
   *  IDs without them). */
  participantDefaultOptions?: Array<{
    value: string;
    label: string;
    description?: string;
    image?: string;
  }>;
}

export function useDetailsFormSchema(
  modeOrOptions: ScheduleMode | UseDetailsFormSchemaOptions,
  apiReferenceArg?: ApiReference
) {
  // Support both old signature (mode, apiReference) and new options object
  const options: UseDetailsFormSchemaOptions =
    typeof modeOrOptions === 'string'
      ? { mode: modeOrOptions, apiReference: apiReferenceArg }
      : modeOrOptions;

  const {
    mode,
    apiReference = 'create',
    interviewPlan,
    meetingRoomsEnabled = false,
    interviewType = 'one_on_one',
    selectedStartDate = null,
    selectedStartTime = '',
    scheduleTimezone,
    calendarBookedStartIso = null,
    calendarBookedEndIso = null,
    visibleWeekStartIso = null,
    disconnectedInterviewerIds,
    meetingRoomGroups,
    meetingRoomsLoading = false,
    candidateName,
    candidateAvatar,
    candidatePhone,
    atsJobStageActionId,
    participantDefaultOptions,
  } = options;

  const scheduleTz =
    scheduleTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // HackerRank: plan carries location='hackerrank' for HR interviews.
  // When true: lock the location field to HackerRank, mount the template
  // selector, and fetch templates.
  const isHackerRank =
    (interviewPlan?.location as string | undefined) === 'hackerrank';

  const { templateOptions: hackerRankTemplateOptions, isLoading: hrLoading } =
    useHackerRankTemplates({ enabled: isHackerRank });

  const calendarHintTime12 =
    calendarBookedStartIso !== null && calendarBookedStartIso !== ''
      ? findClosestTimeOption(formatTime12(calendarBookedStartIso, scheduleTz))
      : '';

  const effectiveStartTimeStr = selectedStartTime || calendarHintTime12 || '';

  const effectiveStartDate: Date | null =
    selectedStartDate ??
    (calendarBookedStartIso ? new Date(calendarBookedStartIso) : null);

  const isManualOrAdhoc = mode === 'manual' || mode === 'adhoc';
  const isSelfSchedule =
    mode === 'self-schedule' || mode === 'self-schedule-adhoc';
  const isAdhoc = mode === 'adhoc' || mode === 'self-schedule-adhoc';
  const isEdit = apiReference === 'edit';

  // For manual/adhoc: single select for one_on_one, multi for panel.
  // For self-schedule: always multi.
  const isMultiInterviewer = isSelfSchedule || interviewType === 'panel';

  // AI-recommended interviewer (manual one-on-one create only, legacy
  // `enableRecommendation`). The backend requires a parseable
  // start_at/end_at window — gate on a booked slot being present.
  const canRecommend =
    !isEdit &&
    mode === 'manual' &&
    !isAdhoc &&
    interviewType === 'one_on_one' &&
    typeof atsJobStageActionId === 'number' &&
    atsJobStageActionId > 0 &&
    Boolean(calendarBookedStartIso && calendarBookedEndIso);

  const { data: recommendedUser, isLoading: isRecommending } =
    useSuggestedInterviewer({
      enabled: canRecommend,
      atsJobStageActionId: atsJobStageActionId ?? 0,
      startAt: calendarBookedStartIso ?? '',
      endAt: calendarBookedEndIso ?? '',
      isTimeslot: true,
      timeZone: scheduleTz,
    });

  const interviewerField = useInterviewerField({
    label: isMultiInterviewer
      ? copy.interviewer.labelPlural
      : copy.interviewer.label,
    placeholder: copy.interviewer.placeholder,
    isMulti: isMultiInterviewer,
    defaultOptions: participantDefaultOptions,
    calendarBookedStartIso,
    scheduleTimezone: scheduleTz,
    atsJobStageActionId,
    visibleWeekStartIso,
    disconnectedInterviewerIds,
  });

  const { fieldConfig: locationField } = useLocationFieldConfig({
    isHackerRank,
    config: {
      label: copy.location.label,
      required: true,
      // preserveValue stops FormBuilder from resetting the field when
      // participant_ids is briefly empty during hydrate / onField races
      // — the plan-driven default stays put until the user changes it.
      condition: {
        operator: 'isNotEmpty',
        field: 'participant_ids',
        preserveValue: true,
      },
    },
  });

  const { fieldConfig: officeField } = useOfficeFieldConfig({
    config: {
      name: 'ats_office_id',
    },
  });

  const noteTakerField = useNoteTakerConfig();

  const { fieldConfig: durationField } = useDurationFieldConfig({
    name: 'duration',
    label: copy.duration.label,
    unitLabel: copy.duration.unit,
  });

  const { fieldConfig: hostField } = useHostFieldConfig({
    name: 'host_id',
    label: copy.host.label,
    placeholder: 'Select host',
    condition: {
      operator: 'equal',
      field: 'location',
      value: 'zoom',
    },
  });

  const { fieldConfig: meetingRoomField } = useMeetingRoomFieldConfig({
    name: 'meeting_room_ids',
    label: copy.meetingRoom.label,
    isMulti: false,
    placeholder: 'Search meeting rooms',
    condition: {
      operator: 'equal',
      field: 'book_meeting_room',
      value: true,
    },
    groupedOptions: meetingRoomGroups,
    loading: meetingRoomsLoading,
    showOptionDividers: true,
    renderOption: renderMeetingRoomOption,
    renderGroupHeader: renderMeetingRoomGroupHeader,
    renderTriggerValue: renderMeetingRoomTriggerValue,
    renderTag: renderMeetingRoomTag,
  });

  // ---- Filtered time options ----
  // startTime: hide past times if selected date is "today" in schedule TZ
  const isSelectedDateToday = effectiveStartDate
    ? moment(effectiveStartDate)
        .tz(scheduleTz)
        .isSame(moment().tz(scheduleTz), 'day')
    : false;
  const nowMins =
    moment().tz(scheduleTz).hours() * 60 + moment().tz(scheduleTz).minutes();

  // If the current start *clock time* is already at or before "now", picking
  // "today" would be invalid — disable today in the date picker (minDate = tomorrow).
  const startTimeParsedForMinDate = effectiveStartTimeStr
    ? parse12hrTime(effectiveStartTimeStr)
    : null;
  const selectedTimeMins = startTimeParsedForMinDate
    ? startTimeParsedForMinDate.hours * 60 + startTimeParsedForMinDate.minutes
    : -1;
  const isWallClockStartTimePastOrEqualNow =
    selectedTimeMins >= 0 && selectedTimeMins <= nowMins;

  const datePickerMinDate = useMemo(
    () =>
      isWallClockStartTimePastOrEqualNow
        ? moment().tz(scheduleTz).add(1, 'day').startOf('day').toDate()
        : moment().tz(scheduleTz).startOf('day').toDate(),
    [isWallClockStartTimePastOrEqualNow, scheduleTz]
  );

  const startTimeOptions = isSelectedDateToday
    ? TIME_OPTIONS.filter((opt) => {
        const p = parse12hrTime(opt.value);
        return p ? p.hours * 60 + p.minutes > nowMins : false;
      })
    : TIME_OPTIONS;

  // endTime: only show times after selected start time (form or calendar hint)
  const startTimeParsed = effectiveStartTimeStr
    ? parse12hrTime(effectiveStartTimeStr)
    : null;
  const startTimeMins = startTimeParsed
    ? startTimeParsed.hours * 60 + startTimeParsed.minutes
    : -1;

  const endTimeOptions =
    startTimeMins >= 0
      ? TIME_OPTIONS.filter((opt) => {
          const p = parse12hrTime(opt.value);
          return p ? p.hours * 60 + p.minutes > startTimeMins : false;
        })
      : TIME_OPTIONS;

  // ---- Compose schema ----
  return useMemo(() => {
    const showScheduleCalendarRow =
      Boolean(interviewPlan) &&
      Boolean(
        interviewPlan?.shared_calendar_enabled ||
        !!(
          interviewPlan?.calendar_info as
            | { provider_calendar_id?: string }
            | undefined
        )?.provider_calendar_id ||
        !!(
          interviewPlan?.scheduled_calendar as
            | { provider_calendar_id?: string }
            | undefined
        )?.provider_calendar_id
      );

    // Meeting room fields — only if interview plan has meeting_rooms_enabled
    const meetingRoomFields = meetingRoomsEnabled
      ? [
          {
            type: 'checkbox' as const,
            name: 'book_meeting_room',
            fieldProps: { label: copy.meetingRoom.checkboxLabel },
          },
          meetingRoomField,
        ]
      : [];

    const schema: unknown[] = [
      {
        type: 'sectionLabel',
        label: copy.sectionLabel,
      },
      {
        type: 'input',
        name: 'interviewName',
        label: copy.interviewName.label,
        required: true,
        fieldProps: {
          placeholder: copy.interviewName.placeholder,
          maxLength: 250,
        },
      },
      {
        type: 'select',
        name: 'interview_type',
        label: copy.interviewType.label,
        required: true,
        options: INTERVIEW_TYPE_OPTIONS,
        fieldProps: { searchable: false },
      },
      {
        ...interviewerField,
        required: true,
        validate: (value: unknown) => {
          if (isMultiInterviewer) {
            if (!Array.isArray(value) || value.length === 0) {
              return 'At least one interviewer is required';
            }
            return true;
          }
          if (value === null || value === undefined || value === '') {
            return 'Interviewer is required';
          }
          return true;
        },
      },
      // Workload / availability banner for picked interviewers.
      // Reads interviewer_status from the plan; banner is null when
      // no flags are set, so we can always mount the custom row.
      {
        type: 'custom' as const,
        name: 'interviewerStatusBanner',
        render: ({
          formMethods,
        }: {
          formMethods: { watch: (_n: string) => unknown };
        }) => {
          const raw = formMethods.watch('participant_ids');
          const ids: Array<string | number> = Array.isArray(raw)
            ? (raw as Array<string | number>)
            : raw
              ? [raw as string | number]
              : [];
          const status = interviewPlan?.interviewer_status as
            | InterviewerStatusMap
            | undefined;
          return <InterviewerStatusBanner selectedIds={ids} status={status} />;
        },
      },
      // AI-recommended interviewer pill (manual one-on-one create).
      // Only mounts while the field is still empty — once the user
      // picks someone (or auto-fills from this pill) it disappears.
      // Ported from KInterviewScheduler SchInterviewerFieldAutoRecommend.
      ...(canRecommend && recommendedUser && !isRecommending
        ? [
            {
              type: 'custom' as const,
              name: 'recommendedInterviewerPill',
              render: ({
                formMethods,
              }: {
                formMethods: {
                  setValue: (_n: string, _v: unknown) => void;
                  watch: (_n: string) => unknown;
                };
              }) => {
                const current = formMethods.watch('participant_ids');
                const hasValue = Array.isArray(current)
                  ? current.length > 0
                  : !!current;
                if (hasValue) return null;
                return (
                  <RecommendedInterviewerPill
                    user={recommendedUser}
                    onSelect={(u) => {
                      formMethods.setValue('participant_ids', String(u.id));
                    }}
                  />
                );
              },
            },
          ]
        : []),
      // Self-schedule only: warn that picking >1 interviewer for a
      // one-on-one means the candidate's slot pick decides who attends.
      // Ported from KInterviewScheduler SchInterviewerField.tsx:1069.
      ...(isSelfSchedule
        ? [
            {
              type: 'custom' as const,
              name: 'multiInterviewerInfo',
              render: ({
                formMethods,
              }: {
                formMethods: { watch: (_n: string) => unknown };
              }) => {
                const type = formMethods.watch('interview_type');
                const ids = formMethods.watch('participant_ids');
                const count = Array.isArray(ids) ? ids.length : 0;
                if (type !== 'one_on_one' || count <= 1) return null;
                return (
                  <Flex
                    flexDir="column"
                    gap="2"
                    mt="3"
                    bg="secondary-50"
                    borderRadius="md"
                    p="3"
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color="secondary-900"
                    >
                      Multiple interviewers in one-on-one interviews
                    </Text>
                    <Text fontSize="sm" color="secondary-700">
                      Since you are sharing the availability of multiple
                      interviewers, one interviewer will be selected based on
                      the time slot picked by the candidate.
                    </Text>
                  </Flex>
                );
              },
            },
          ]
        : []),
      ...meetingRoomFields,
      {
        type: 'sectionLabel',
        label: copy.scheduleSectionLabel,
      },
      {
        ...durationField,
        required: true,
        validate: (value: unknown) =>
          typeof value === 'number' && value > 0
            ? true
            : 'Duration must be greater than 0',
      },
      ...(showScheduleCalendarRow
        ? [
            {
              type: 'custom' as const,
              name: 'calendar_info',
              render: ({
                field,
                formMethods,
              }: {
                field: { value: unknown };
                formMethods: {
                  setValue: (_n: string, _v: unknown, _o?: unknown) => void;
                  watch: (_n: string) => unknown;
                };
              }) => (
                <ScheduleCalendarRowField
                  interviewPlan={interviewPlan}
                  apiReference={apiReference}
                  field={field}
                  formMethods={formMethods}
                />
              ),
            },
          ]
        : []),
      // Date/Time — manual/adhoc only
      // Info bar (shown when not editing)
      ...(isManualOrAdhoc
        ? [
            // Register isDateTimeEditing as a form field so conditions can watch it
            {
              type: 'input' as const,
              name: 'isDateTimeEditing',
              hidden: true,
            },
            // Info bar — renders null when in edit mode
            {
              type: 'custom' as const,
              name: 'dateTime',
              render: ({
                field,
                formMethods,
              }: {
                field: { value: unknown };
                formMethods: {
                  setValue: (_n: string, _v: unknown) => void;
                  watch: (_n: string) => unknown;
                };
              }) => {
                const mode = formMethods.watch('isDateTimeEditing');
                if (mode === 'edit') return null;
                return (
                  <DateTimeField
                    value={typeof field.value === 'string' ? field.value : ''}
                    onEdit={() =>
                      formMethods.setValue('isDateTimeEditing', 'edit')
                    }
                  />
                );
              },
            },
            // Start date (edit mode)
            {
              type: 'date' as const,
              name: 'startDate',
              label: 'Start date',
              required: 'Start date is required',
              condition: isDateTimeEditingCondition,
              fieldProps: {
                mode: 'single',
                triggerWidth: '100%',
                calendarProps: { minDate: datePickerMinDate },
              },
            },
            // Start time + End time (edit mode, side by side)
            {
              type: 'section' as const,
              columns: 2,
              condition: isDateTimeEditingCondition,
              fields: [
                {
                  type: 'select' as const,
                  name: 'startTime',
                  label: 'Start time',
                  required: 'Start time is required',
                  options: startTimeOptions,
                  colSpan: 1,
                  fieldProps: { searchable: true },
                },
                {
                  type: 'select' as const,
                  name: 'endTime',
                  label: 'End time',
                  required: 'End time is required',
                  options: endTimeOptions,
                  colSpan: 1,
                  fieldProps: { searchable: true },
                  validate: (
                    value: unknown,
                    allValues: Record<string, unknown>
                  ) => {
                    if (!value || !allValues.startTime) return true;
                    const start = parse12hrTime(allValues.startTime as string);
                    const end = parse12hrTime(value as string);
                    if (!start || !end) return true;
                    const startMin = start.hours * 60 + start.minutes;
                    const endMin = end.hours * 60 + end.minutes;
                    if (endMin <= startMin)
                      return 'End time must be after start time';
                    return true;
                  },
                },
              ],
            },
          ]
        : []),
      {
        type: 'switch' as const,
        name: 'calendar_visibility',
        fieldProps: {
          switchLabel: copy.calendarVisibility.label,
          description: copy.calendarVisibility.subLabel,
          fullWidth: true,
          labelAlign: 'center',
          labelPosition: 'left',
        },
      },
      locationField,
      {
        type: 'select' as const,
        name: 'ext_interview_template_id',
        label: 'HackerRank Template',
        placeholder: 'Select template',
        options: hackerRankTemplateOptions,
        loading: hrLoading,
        condition: {
          operator: 'equal',
          field: 'location',
          value: 'hackerrank',
        },
      },
      // Candidate phone — read-only block with an edit pencil that
      // swaps the schema row for a FormBuilder phone input. The edit
      // state lives on the form so the condition below can watch it.
      // Always mount when the schema is built — the per-row `location`
      // condition gates visibility. The previous outer guard
      // (`candidatePhone ? ... : []`) silently unmounted the whole
      // block when the candidate had no phone on record, which made
      // it impossible to enter one.
      ...[
        {
          type: 'input' as const,
          name: 'isCandidatePhoneEditing',
          hidden: true,
        },
        {
          // NOTE: `condition` on `type: 'custom'` is NOT honoured by
          // the current FormBuilder (LayoutDispatcher bypasses the
          // ConditionalField wrapper for the custom branch). We
          // guard on `location` inside the render callback instead.
          // Switch to the declarative `condition` if kula-ai/components
          // patches LayoutDispatcher upstream.
          type: 'custom' as const,
          name: 'candidatePhoneDisplay',
          render: ({
            formMethods,
          }: {
            formMethods: {
              setValue: (_n: string, _v: unknown) => void;
              watch: (_n: string) => unknown;
            };
          }) => {
            if (formMethods.watch('location') !== 'phone_interview') {
              return null;
            }
            if (formMethods.watch('isCandidatePhoneEditing') === 'edit') {
              return null;
            }
            return (
              <CandidatePhoneField
                candidateName={candidateName}
                candidateAvatar={candidateAvatar}
                phoneNumber={candidatePhone}
                onEdit={() =>
                  formMethods.setValue('isCandidatePhoneEditing', 'edit')
                }
              />
            );
          },
        },
        {
          type: 'phone' as const,
          name: 'candidate_phone',
          label: 'Candidate phone',
          required: 'Candidate phone is required',
          validate: (value: unknown) => {
            const v = value as
              | { dialCode?: string; number?: string }
              | undefined;
            const num = v?.number?.trim() ?? '';
            if (!num) return 'Candidate phone is required';
            return true;
          },
          condition: {
            operator: 'and' as const,
            conditions: [
              {
                operator: 'equal' as const,
                field: 'location',
                value: 'phone_interview',
              },
              {
                operator: 'equal' as const,
                field: 'isCandidatePhoneEditing',
                value: 'edit',
              },
            ],
          },
          fieldProps: { defaultCountry: 'US' },
        },
      ],
      hostField,
      officeField,
      {
        // AI note-taker should only appear when an interviewer is
        // picked AND the meeting platform supports it. Location is
        // already hidden until participant_ids is filled, but its
        // *value* still defaults to zoom/meet behind the scenes —
        // without this extra guard the switch leaks through.
        ...noteTakerField,
        condition: {
          operator: 'and',
          conditions: [
            { operator: 'isNotEmpty', field: 'participant_ids' },
            {
              operator: 'in',
              field: 'location',
              value: ['zoom', 'google_meet', 'teams'],
            },
          ],
        },
      },
      {
        type: 'switch' as const,
        name: 'fill_with_ai_enabled',
        condition: {
          operator: 'and',
          conditions: [
            { operator: 'isNotEmpty', field: 'participant_ids' },
            { operator: 'equal', field: 'note_taker_enabled', value: true },
            {
              operator: 'in',
              field: 'location',
              value: ['zoom', 'google_meet', 'teams'],
            },
          ],
        },
        fieldProps: {
          switchLabel: 'Allow interviewers to complete scorecards with AI?',
          description:
            'Helps draft scorecard faster, improving completion rates and consistency',
          fullWidth: true,
          labelAlign: 'center',
          labelPosition: 'left',
        },
      },
    ];

    return schema;
  }, [
    interviewPlan,
    meetingRoomsEnabled,
    meetingRoomField,
    interviewerField,
    durationField,
    isManualOrAdhoc,
    datePickerMinDate,
    startTimeOptions,
    endTimeOptions,
    locationField,
    hostField,
    officeField,
    noteTakerField,
    isMultiInterviewer,
    isSelfSchedule,
    apiReference,
    hackerRankTemplateOptions,
    hrLoading,
    candidateName,
    candidateAvatar,
    candidatePhone,
    canRecommend,
    isRecommending,
    recommendedUser,
  ]);
}
